import { io } from "socket.io-client";

import { getUserId, log, logError, setRoomId, setUserId } from "../utils.js";
import { RTCPeer, RTCSignalType } from "./RTCPeer.js";


/**
 * Class representing a RTC signaling client capable of negotiating a 
 * peer-to-peer data channel.
 */
export class SignalingClient {
    /**
     * @param {string} serverEndpoint For example "ws://localhost:3333"
     * @param {RTCPeer} rtcPeer 
     * @param {object} config
     * @param {(response: any)=>void} config.handleJoinResponse A function which
     *      will be called when the join response is received after joining a room.
     *      This can be used, for example, to update the UI appropriately.
     * @param {String} config.dataChannelName The Name for the RTC datachannel, which will
     *      be set up.
     * @param {()=>void} config.handlePeerLeft
     */
    constructor(serverEndpoint, rtcPeer, config) {
        this._socket = io(serverEndpoint, {
            auth: {
                userId: getUserId(),
            },
            autoConnect: false,
        });
        this._setupBasicListeners(rtcPeer);
        this._config = config;
    }

    get socket() { return this._socket; }

    /** @param {RTCPeer} rtcPeer The RTCPeer instance to be set up. */
    _setupBasicListeners(rtcPeer) {
        this.socket.on("connect", () => {
            log("[SignalingClient] Connected to signaling server");
        });
        this.socket.on("disconnect", () => {
            if (this.socket.active) {
                log("[SignalingClient] Connection interrupted. Trying to reconnect.");
            } else {
                log("[SignalingClient] Disconnected from signaling server.");
            }
        });

        /**
         * @typedef Auth
         * @property {string} userId
        */
        // Fired when the server generated and send an auth object for this client.
        this.socket.on("receiveAuth", (/** @type Auth */ auth) => {
            log(`[SignalingClient] Received auth object: '${JSON.stringify(auth)}'`);
            setUserId(auth.userId);
            // This is only relevant in case of a reconnect
            this.socket.auth = auth;
        });

        this.socket.on("error", ({ msg }) => { logError(msg); });

        /// Peers are ready to start RTC negotiations
        this.socket.on("ready", async ({ initiator }) => {
            log("[SignalingClient] Peers are ready.");
            await this._setupRTCConnection(rtcPeer, initiator);
        });

        this.socket.on("peerLeft", () => {
            log("[SignalingClient] A peer left the room.");
            rtcPeer.close();
            this._config.handlePeerLeft();
        });
    }

    /**
     * Establishes the underlying RTCPeerConnection and sets up the RTC-datachannel
     * of the given RTCPeer instance.
     * This function is meant to be called once per session after the signaling server
     * send a "ready" event, notifying this client that a peer joined the same room
     * and is ready to start the connection (negotiation).
     * 
     * @param {RTCPeer} rtcPeer The RTCPeer whose connection to configure. 
     * @param {boolean} initiator Whether this client is repsonsible for creating 
     *      and sending the RTC-offer.
     */
    async _setupRTCConnection(rtcPeer, initiator) {
        log("[SignalingClient] Initiating RTC negotiations.");

        if (rtcPeer.connection.connectionState !== "new") {
            rtcPeer.reset();
        }

        if (!rtcPeer.connection) {
            throw new Error("'rtcPeer.connection' reference holds invalid value");
        }

        rtcPeer.connection.onicecandidate = ({/** @type RTCIceCandidate|null */ candidate }) => {
            log(`[RTCPeerConnection] Received ICE candidate '${candidate?.candidate}'`);
            if (candidate !== null) {
                log("[RTCPeerConnection] Sending candidate to peer.");
                this.socket.emit("signal", {
                    type: RTCSignalType.ICE_CANDIDATE,
                    candidate
                });
            } else {
                log("[RTCPeerConnection] All candidates received and proposed to other peer.");
            }
        };

        rtcPeer.connection.ondatachannel = ({ /** @type RTCDataChannel */ channel }) => {
            rtcPeer.setupDataChannel(channel);
            log("[RTCPeerConnection] Received and registered a dataChannel.");
        };

        if (initiator) {
            // Create and send offer
            log("[RTCPeerConnection] Ready. Creating dataChannel and offer (-> INITIATOR).");
            const dataChannel = rtcPeer.connection.createDataChannel(this._config.dataChannelName);
            rtcPeer.setupDataChannel(dataChannel);

            const offer = await rtcPeer.connection.createOffer();
            await rtcPeer.connection.setLocalDescription(offer);

            log("[RTCPeerConnection] Sending the offer to the peer.");
            this.socket.emit("signal", { type: RTCSignalType.OFFER, offer })
        }

        // Remove old listeners in case of new or reconnection
        this.socket.off("signal");

        // Listen for (RTC) signaling messages from other peer and react based on 
        // the signal type.
        this.socket.on("signal", async ({ type, offer, answer, candidate }) => {
            log(`[SignalingClient] Received '${type}' signal.`);
            if (type === RTCSignalType.OFFER) {
                if (initiator) {
                    logError("unexpected offer received (this peer is the initiator)");
                    return;
                }
                await rtcPeer.connection.setRemoteDescription(offer);
                const answer = await rtcPeer.connection.createAnswer();
                await rtcPeer.connection.setLocalDescription(answer);

                log("[SignalingClient] Sending answer to peer.")
                this.socket.emit("signal", { type: RTCSignalType.ANSWER, answer });

            } else if (type === RTCSignalType.ANSWER) {
                if (!initiator) {
                    logError("[SignalingClient] Unexpected answer received (this peer is NOT the initiator).");
                    return;
                }
                await rtcPeer.connection.setRemoteDescription(answer);

            } else if (type === RTCSignalType.ICE_CANDIDATE) {
                await rtcPeer.connection.addIceCandidate(candidate);
            }
        });
    }

    /**
     * @param {String|null} roomId The target roomId. `null` requests new room.
     */
    createOrJoinRoom(roomId) {
        if (!this.socket.connected) {
            this.socket.connect();
        }

        log("[SignalingClient] " + (
            roomId ? `Joining room with Id '${roomId}'.`
                : "No roomId provided. Requesting new room."
        ));

        this.socket.emit("join", roomId, (response) => { this.handleJoinResponse(response); });
    }

    /**
     * @param {Object} response
     * @param {String=} response.roomId - The room's id the peer has joined.
     */
    handleJoinResponse(response) {
        const { roomId } = response;
        if (roomId) {
            setRoomId(roomId);
        } else {
            throw new Error(`response contained invalid roomId: ${roomId}`);
        }

        log(`[SignalingClient] Joined (signaling) room '${roomId}'`)

        this._config.handleJoinResponse(response);
    }

    /** Disconnect from the signaling server. */
    disconnect() {
        this.socket.disconnect();
    }
}

